import Head from 'next/head';
import { AdBox, AdSlots } from '../components/ad-box.component';
import * as React from 'react';

import { ConverterFromDescription, ConverterToDescription, Description, RouteProps, UnitConverterState } from '../types/route.types';
import convert, { ConvertUtils } from '../utils/convert-utils';
import styles from './converter.component.module.scss';
// import FreestarAdSlot from '@freestar/pubfig-adslot-react-component'
import { readFileSync } from 'fs';
import { groupBy, includes, isNumber } from 'lodash-es';
import Link from 'next/link';
import SearchBox, { VIEW_API } from '../components/search-box/search-box.component';
import { NextRouter, useRouter } from 'next/router';
import path from 'path';
import { ParsedUrlQuery } from 'querystring';
import ConversionWidgetComponent, { ConversionWidgetEvent } from '../components/conversion-widget/convertions-widget.component';
import { Measure, UnitConversion } from '../types/types';
import { unitConversionFormatter, unitFormatter } from '../utils/unit-formatter';
import { NeighboursBlock, PopularUnitBlock } from '../components/link-blocks.component';
import type { UnitPairBlocks } from '../utils/link-hierarchy';
import { applyUnitPairPolicy, hubLinkBlocks } from '../utils/link-hierarchy.server';

const SSR_DATA_DIR = path.resolve(process.cwd(), 'ssr-data', 'build', 'route-content');

// Computed once per build worker, not per page
let _allMeasures: { measure: string; measureName: string }[] | null = null;
function getAllMeasures() {
    if (_allMeasures) return _allMeasures;
    const allConversions = JSON.parse(readFileSync(path.join(SSR_DATA_DIR, 'all-unit-conversion-data.json'), 'utf8'));
    const byMeasure = groupBy(allConversions, 'measure') as { [key: string]: any[] };
    _allMeasures = Object.values(byMeasure).map((units) => ({
        measure: units[0].measure,
        measureName: units[0].measureName,
    }));
    return _allMeasures;
}

export async function getStaticPaths() {
    const routesDataFile = path.join(SSR_DATA_DIR, 'all-convert-routes.json');

    const routesData = readFileSync(routesDataFile, 'utf8')
    // Find hub pages
    const hubFiles = require('fs').readdirSync(SSR_DATA_DIR).filter((f: string) => f.startsWith('hub-') && f.endsWith('.json'));
    const hubPaths = hubFiles.map((f: string) => ({ params: { id: f.replace('hub-', '').replace('.json', '') } }));

    return {
        paths: [
            ...JSON.parse(routesData).map(r => ({ params: r })),
            ...hubPaths,
        ],
        fallback: false
    };
}

// This also gets called at build time
export async function getStaticProps({ params }) {
    // Check if it's a hub page
    const hubFile = path.join(SSR_DATA_DIR, `hub-${params.id}.json`);
    if (require('fs').existsSync(hubFile)) {
        const data = JSON.parse(readFileSync(hubFile, 'utf8'));
        // Link hierarchy (METHOD §4, UNIT hub): the hub lists UNIT-HV pairs first, then every pair by from-unit group, or —
        // for measures above CAP_INDEX pairs — one base pair (X -> base unit) per from-unit; the base pair's side panel
        // lists its whole group, so every pair stays <= 2 hops from the hub. Order/selection come from the vendored
        // policy (data/link-hierarchy), never from a hand-written list; an unknown hub falls back to the generator list.
        const linkBlocks = hubLinkBlocks(params.id);
        const all: any[] = data.conversions || [];
        const byUrl = new Map<string, any>(all.map((c: any) => [c.url, c]));
        const listed = linkBlocks.hubList.map(u => byUrl.get(u)).filter(Boolean);
        const conversions = listed.length > 0 ? listed : all;
        return { props: { ...params, ...data, conversions, conversionCount: all.length, linkBlocks, pageFor: 'hub' } };
    }
    if (params.id.includes('-to-')) {
        return getStaticPropsForConvertPages({ params });
    }
    return getStaticPropsForChildPages({ params });
}


export async function getStaticPropsForChildPages({ params }) {
    const filePath = path.join(SSR_DATA_DIR, `${params.id}.json`);
    const data = readFileSync(filePath, 'utf8');
    return { props: { ...params, ...JSON.parse(data), pageFor: 'child' } };
}


export async function getStaticPropsForConvertPages({ params }) {
    const filePath = path.join(SSR_DATA_DIR, `${params.id}.json`);
    const data = readFileSync(filePath, 'utf8');
    // Link hierarchy (METHOD §4, UNIT pair): popular-unit / neighbours / group-links|group-list / file-tools /
    // more-converters computed from the vendored policy + this page's from-unit group; side-panel list trimmed to what
    // the policy lets this page link; generator-injected in-content anchors outside that set are unlinked.
    const { linkBlocks, routeData } = applyUnitPairPolicy(JSON.parse(data));

    return { props: { ...params, ...routeData, linkBlocks, pageFor: 'converter', allMeasures: getAllMeasures() } };
}

interface UnitConverterProps extends RouteProps {
    router: NextRouter;
    pathname: string;
    query?: ParsedUrlQuery;
}

class UnitConverter extends React.Component<UnitConverterProps, UnitConverterState> {
    fromTypeSupportedTargetConverters: ConverterFromDescription[] = [];
    toTypeSupportedTargetConverters: ConverterToDescription[] = [];

    filteredFromTypeSupportedTargetConverters: ConverterFromDescription[] = [];
    filteredToTypeSupportedTargetConverters: ConverterToDescription[] = [];

    sidePanelLinks = [];

    fromDescription: Description;
    toDescription: Description;

    selectedFrom: any;
    selectedTo: any;

    inputRef = React.createRef<HTMLInputElement>();
    fromInputRef = React.createRef<HTMLInputElement>();
    toInputRef = React.createRef<HTMLInputElement>();

    targeting = {};
    private tableDebounce: any = null;
    private measureCache: { [measure: string]: UnitConversion[] } = {};

    constructor(props: UnitConverterProps) {
        super(props);
        this.fromDescription = (props as any).fromDescription
        this.toDescription = (props as any).toDescription
        this.fromTypeSupportedTargetConverters = (props as any).fromTypeSupportedTargetConverters;
        this.sidePanelLinks = (props as any).sidePanelLinks
        this.selectedFrom = (props as any).selectedFrom
        this.toTypeSupportedTargetConverters = (props as any).toTypeSupportedTargetConverters
        this.selectedTo = (props as any).selectedTo

        const conversions: UnitConversion[] = props['unitConversions'];
        const measures: Measure[] = (props as any).allMeasures || [];

        // Seed cache with SSR-provided conversions for current measure
        this.measureCache[this.fromDescription.measure] = conversions;

        const measure = this.fromDescription.measure;
        var initialMeasure = measures.find((a) => a.measure == measure);
        if (!initialMeasure) {
            initialMeasure = measures[0];
        }
        // console.log('unitConversions ======', initialMeasure);
        this.state = {
            ...(props as any).state,
            selectedFromUnit: this.fromDescription.abbr,
            selectedToUnit: this.toDescription.abbr,
            initialMeasure: initialMeasure,
            unitConversions: conversions,
            measures: measures,
            currentPath: this.props.pathname,
            currentQuery: this.props.query,
        }

        this.filteredFromTypeSupportedTargetConverters = this.fromTypeSupportedTargetConverters.filter(v => v.fromUnit !== this.toDescription.abbr)
        this.filteredToTypeSupportedTargetConverters = this.toTypeSupportedTargetConverters.filter(v => v.toUnit !== this.fromDescription.abbr)
    }

    componentDidUpdate(prevProps) {
        if (this.props.id !== prevProps.id || this.props.pathname !== prevProps.pathname) {
            // Refresh instance variables from new page props
            this.fromDescription = (this.props as any).fromDescription;
            this.toDescription = (this.props as any).toDescription;
            this.fromTypeSupportedTargetConverters = (this.props as any).fromTypeSupportedTargetConverters;
            this.toTypeSupportedTargetConverters = (this.props as any).toTypeSupportedTargetConverters;
            this.sidePanelLinks = (this.props as any).sidePanelLinks;
            this.selectedFrom = (this.props as any).selectedFrom;
            this.selectedTo = (this.props as any).selectedTo;
            this.filteredFromTypeSupportedTargetConverters = this.fromTypeSupportedTargetConverters.filter(v => v.fromUnit !== this.toDescription.abbr);
            this.filteredToTypeSupportedTargetConverters = this.toTypeSupportedTargetConverters.filter(v => v.toUnit !== this.fromDescription.abbr);

            const selectedFromValueStr = (this.getQueryParams() || {}).value;
            const state = {
                currentPath: this.props.pathname,
                selectedFromUnit: this.props.from.unit,
                selectedToUnit: this.props.to.unit,
                unitConversions: this.props['unitConversions'],
                ...(selectedFromValueStr && selectedFromValueStr.length > 0 && isNumber(selectedFromValueStr) ? { selectedFromValue: parseFloat(selectedFromValueStr) } : null),
            }
            this.setState(state);
        }
    }

    private getFromPlural() {
        return convert().describe(this.props.from.unit).plural;
    }

    private getMeasureName() {
        // Canonical name, computed once by the generator (measures.ts) and carried in the
        // route data — do not re-derive it here, or the breadcrumb drifts from the <h3>.
        return this.props.from?.readableType || '';
    }

    private getToPlural() {
        return convert().describe(this.props.to.unit).plural;
    }

    private getFromAbbr() {
        return unitFormatter(convert().describe(this.props.from.unit).abbr);
    }

    private getToAbbr() {
        return unitFormatter(convert().describe(this.props.to.unit).abbr);
    }

    componentDidMount(): void {
        // Track page view
        if (this.props.id) {
            fetch(`${VIEW_API}/${this.props.id}`, { method: 'POST' }).catch(() => {});
        }
    }

    valueChanged() {
        this.setState({ inputValue: this.inputRef.current.value as any });
        if (this.tableDebounce) clearTimeout(this.tableDebounce);
        this.tableDebounce = setTimeout(() => {
            const objForState = {};
            if (this.props.from) {
                const possibilities = convert().possibilities(this.props.from.type as any);
                for (const possibility of possibilities) {
                    if (possibility !== this.props.from.unit) {
                        const floatValue = parseFloat(this.inputRef.current.value);
                        const value = floatValue || (floatValue == 0 ? 0 : 1);
                        objForState[`${this.props.from.type}***${this.props.from.unit}***${possibility}`] =
                            ConvertUtils.convertTo(value, this.props.from.unit as any, possibility);
                    }
                }
            }
            this.setState(objForState);
        }, 150);
    }

    private conversionTable(): React.ReactNode {
        if (this.props.commonValuesConversionTable) {
            const hasBinary = !!(this.props as any).binaryToAbbr;
            const binaryAbbr = (this.props as any).binaryToAbbr;
            return <div className={styles.section}>
                <h2>{this.getFromPlural()} to {this.getToPlural()} conversion table</h2>
                <div className={styles['table-responsive']}>
                    <table className={styles['unit-conversion-table']}>
                        <thead>
                            <tr>
                                <th scope="col">{this.getFromPlural()} ({this.getFromAbbr()})</th>
                                <th scope="col">{this.getToPlural()} ({this.getToAbbr()})</th>
                                {hasBinary && <th scope="col">{binaryAbbr} <span className={styles['quick-answer-label']}>binary</span></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {
                                this.props.commonValuesConversionTable.map((v: any, i) => {
                                    return <tr key={`a${i}`}>
                                        <td>{v.fromValue}</td>
                                        <td>{v.toValue}</td>
                                        {hasBinary && <td>{v.binaryToValue}</td>}
                                    </tr>
                                })
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        }
        return ""
    }

    private getPageSummary(): React.ReactNode {
        if (!this.props.pageSummary) {
            return <div className={styles.section}>
                <h2>How to convert {this.getFromPlural()} to {this.getToPlural()}</h2>
                <p>
                    1 {this.getFromPlural()} ({this.getFromAbbr()}) is equal to {ConvertUtils.convertTo(1, this.props.from.unit as any, this.props.to.unit as any)} {this.getToPlural()} ({this.getToAbbr()}).
                </p>
                <p>
                    1 {this.getFromAbbr()} = {ConvertUtils.convertTo(1, this.props.from.unit as any, this.props.to.unit as any)} {this.getToAbbr()} <br />
                    or <br />
                    1 {this.getToAbbr()} = {ConvertUtils.convertTo(1, this.props.to.unit as any, this.props.from.unit as any)} {this.getFromAbbr()}
                </p>
            </div>
        }
        return <div className={styles.section}>
            <div dangerouslySetInnerHTML={{ __html: this.props.pageSummary }}></div>
        </div>
    }

    private getHowtoSection(): React.ReactNode {
        if (!this.props.howtoContent) return null;
        return <div className={styles.section}>
            <div dangerouslySetInnerHTML={{ __html: this.props.howtoContent }}></div>
        </div>
    }

    private getFaqSection(): React.ReactNode {
        if (!this.props.faqContent) return null;
        return <div className={styles.section}>
            <h2>Frequently Asked Questions</h2>
            <div dangerouslySetInnerHTML={{ __html: this.props.faqContent }}></div>
        </div>
    }

    private getFaqJsonLd(): any {
        return this.props.faqJsonLd || null;
    }

    private getHowtoJsonLd(): any {
        return this.props.howtoJsonLd || null;
    }


    private async fetchMeasureData(measure: string): Promise<UnitConversion[]> {
        if (this.measureCache[measure]) return this.measureCache[measure];
        const res = await fetch(`/unit-converter/route-data/measures/${measure}.json`);
        const data: UnitConversion[] = await res.json();
        this.measureCache[measure] = data;
        return data;
    }

    handleConversionWidgetEvent(event: ConversionWidgetEvent) {
        // Swap: both units changed — need fallback since reverse pair may not be in SSR data
        if (includes(event.changedProperties, 'fromUnit') && includes(event.changedProperties, 'toUnit')) {
            const fromPlural = convert().describe(event.fromUnit).plural.toLowerCase().replace(/ /g, '-');
            const toPlural = convert().describe(event.toUnit).plural.toLowerCase().replace(/ /g, '-');
            this.props.router.replace(`/${fromPlural}-to-${toPlural}/?measure=${event.measure}&value=${event.fromValue}`);
            return;
        }

        const unitConversion = this.state.unitConversions.find((a) => a.fromUnit == event.fromUnit && a.toUnit == event.toUnit);
        if (unitConversion) {
            const url = UnitConversion.linkUrlPart(unitConversion);
            this.setState({
                initialMeasure: this.state.measures.find(a => a.measure == event.measure),
            });
            if (includes(event.changedProperties, 'measure')) {
                this.fetchMeasureData(event.measure).then(conversions => {
                    const unitConversionForMeasure = conversions
                        .find((a) => a.measure == event.measure && a.fromUnit != a.toUnit);
                    const unitConversionURL = UnitConversion.linkUrlPart(unitConversionForMeasure);
                    this.props.router.replace(`/${unitConversionURL}/?measure=${event.measure}`);
                });
            } else if (includes(event.changedProperties, 'fromUnit')) {
                const unitConversionForMeasure = this.state.unitConversions
                    .find((a) => a.fromUnit == event.fromUnit && a.toUnit == this.state.selectedToUnit);
                if (unitConversionForMeasure.fromUnit == unitConversionForMeasure.toUnit) {
                    return;
                }
                const unitConversionURL = UnitConversion.linkUrlPart(unitConversionForMeasure);
                this.props.router.replace(`/${unitConversionURL}/?measure=${event.measure}&value=${event.fromValue}`);
            } else if (includes(event.changedProperties, 'toUnit')) {
                const unitConversionForMeasure = this.state.unitConversions
                    .find((a) => a.fromUnit == this.state.selectedFromUnit && a.toUnit == event.toUnit);
                if (unitConversionForMeasure.fromUnit == unitConversionForMeasure.toUnit) {
                    return;
                }
                const unitConversionURL = UnitConversion.linkUrlPart(unitConversionForMeasure);
                this.props.router.replace(`/${unitConversionURL}/?measure=${event.measure}&value=${event.fromValue}`);
            } else if (includes(event.changedProperties, 'fromValue')) {
                const unitConversionForMeasure = this.state.unitConversions
                    .find((a) => a.fromUnit == this.state.selectedFromUnit && a.toUnit == this.state.selectedToUnit);
                const unitConversionURL = UnitConversion.linkUrlPart(unitConversionForMeasure);
                window.history.replaceState({ value: event.fromValue, measure: event.measure }, '', `/unit-converter/${unitConversionURL}/?measure=${event.measure}&value=${event.fromValue}`);
            }
        }
    }

    private getQueryParams() {
        const searchParams = new URLSearchParams(this.props.router.asPath.substring(this.props.router.asPath.indexOf("?")));

        const measure = searchParams.get('measure') || undefined;
        const value = searchParams.get('value');

        return {
            measure,
            value,
        };
    }

    render() {
        const faqJsonLd = this.getFaqJsonLd();
        const howtoJsonLd = this.getHowtoJsonLd();
        // Link-hierarchy blocks (pages/[id].tsx::getStaticPropsForConvertPages -> utils/link-hierarchy.ts). sidePanelLinks
        // already holds only the group members this page may link (HV source: whole group HV-first, capped; LV source: HV
        // members + neighbours; base pair: whole group), keyed by link for the pills, table rows and side panel.
        const lb: UnitPairBlocks | undefined = (this.props as any).linkBlocks;
        const sidePanelLinks: any[] = this.sidePanelLinks || [];
        const byLink = new Map<string, any>(sidePanelLinks.map((l: any) => [l.link, l]));
        const linkByRow = new Map<string, string>(sidePanelLinks.map((l: any) => [`${l.readableToPlural}|${l.readableToAbbr}`, l.link]));
        const groupBlock = lb && lb.groupList.length > 0 ? 'group-list' : 'group-links';
        const groupUrls: string[] = lb ? (lb.groupList.length > 0 ? lb.groupList : lb.groupLinks) : sidePanelLinks.map((l: any) => l.link);
        const groupEntries = groupUrls.map(u => byLink.get(u)).filter(Boolean);
        const pillEntries = groupEntries.slice(0, 8);
        return <React.Fragment>
            <Head>
                <link rel="canonical" href={`https://www.xconvert.com${this.props.url}`} />
                <link rel="alternate" href={`https://www.xconvert.com/unit-converter/${this.props.id?.split('-to-').reverse().join('-to-')}`} title={`${this.getToPlural()} to ${this.getFromPlural()}`} />
                {faqJsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />}
                {howtoJsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(howtoJsonLd) }} />}
                {(this.props as any).tableJsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify((this.props as any).tableJsonLd) }} />}
            </Head>

            {/* Hero Section */}
            <div className={styles['main-section']}>
                <SearchBox />
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item"><a href="https://www.xconvert.com">Home</a></li>
                        <li className="breadcrumb-item"><Link href="/">Unit Converter</Link></li>
                        <li className="breadcrumb-item"><Link href={`/${this.props.from?.type}`}>{this.getMeasureName()}</Link></li>
                        <li className="breadcrumb-item active" aria-current="page">{this.getFromPlural()} to {this.getToPlural()}</li>
                    </ol>
                </nav>
                <h1>{this.getFromPlural()} ({this.getFromAbbr()}) to {this.getToPlural()} ({this.getToAbbr()}) conversion</h1>
                <AdBox slot={AdSlots.pageTop} variant="banner" />
                <div className={styles['convert-widget']}>
                    <ConversionWidgetComponent
                        unitConversions={this.state.unitConversions}
                        measures={this.state.measures}
                        initialMeasure={this.state.initialMeasure}
                        selectedFromUnit={this.state.selectedFromUnit}
                        selectedToUnit={this.state.selectedToUnit}
                        selectedFromValue={this.state.selectedFromValue}
                        onChange={(e) => this.handleConversionWidgetEvent(e)}
                    ></ConversionWidgetComponent>
                </div>
                <div className={styles['hero-meta']}>
                    {this.props.quickAnswer && (
                        <span className={styles['hero-fact']}>
                            1 {this.props.quickAnswer.decimal.fromAbbr} = <strong>{this.props.quickAnswer.decimal.value}</strong> {this.props.quickAnswer.decimal.toAbbr}
                            {this.props.quickAnswer.binary && (
                                <> <span className={styles['hero-sep']}>|</span> 1 {this.props.quickAnswer.binary.fromAbbr} = <strong>{this.props.quickAnswer.binary.value}</strong> {this.props.quickAnswer.binary.toAbbr} <span className={styles['quick-answer-label']}>binary</span></>
                            )}
                        </span>
                    )}
                    <Link href={`/${this.props.id?.split('-to-').reverse().join('-to-')}`} className={styles['reverse-link']}>
                        {this.getToAbbr()} → {this.getFromAbbr()}
                    </Link>
                </div>
                {lb && <NeighboursBlock links={lb.neighbours} />}

                <AdBox slot={AdSlots.belowTool} variant="rect" />

                {/* Alert/Note Section */}
                {this.state.possibleOptions && (
                    <>
                        {this.state.possibleOptions.to == 'decimal' && (
                            <div className={styles['alert-info']} data-block="body">
                                <strong>Note:</strong> Above conversion to {this.state.possibleOptions.currentToAbbr} is base 2 binary units. If you want to use base 10 (decimal unit) use <a href={this.state.possibleOptions.link}>{this.state.possibleOptions.readableText}</a> (which results to {this.state.possibleOptionsValue} {this.state.possibleOptions.toAbbr}).
                                See the difference between <a href='https://en.wikipedia.org/wiki/Metric_prefix'>decimal (Metric)</a> and <a href='https://en.wikipedia.org/wiki/Binary_prefix'>binary</a> prefixes.
                            </div>
                        )}

                        {this.state.possibleOptions.to == 'binary' && (
                            <div className={styles['alert-info']} data-block="body">
                                <strong>Note:</strong> Above conversion to {this.state.possibleOptions.currentToAbbr} is base 10 decimal unit. If you want to use base 2 (binary unit) use <a href={this.state.possibleOptions.link}>{this.state.possibleOptions.readableText}</a> (which results to {this.state.possibleOptionsValue} {this.state.possibleOptions.toAbbr}).
                                See the difference between <a href='https://en.wikipedia.org/wiki/Metric_prefix'>decimal (Metric)</a> and <a href='https://en.wikipedia.org/wiki/Binary_prefix'>binary</a> prefixes.
                            </div>
                        )}
                    </>
                )}

                {/* "Which <X>?" disambiguation — compact callout right under the widget/answer
                    (explicit about what THIS page is + links to the sibling variants), instead
                    of a large table buried lower down. Keeps the tool first, box above the fold. */}
                {/* Unchanged in-content disambiguation links (link-hierarchy block class "body": the engine leaves them
                    as they are; an LV page may link its sibling variant here). */}
                {(this.props as any).variantComparison && (
                    <div className={styles['alert-info']} data-block="body">
                        <strong>{(this.props as any).variantComparison.heading}</strong>{' '}
                        {(this.props as any).variantComparison.note}{' '}
                        {(this.props as any).variantComparison.variants.map((v: any, i: number, arr: any[]) => (
                            <span key={i}>
                                {v.isThisPage
                                    ? <strong>{v.label} ({v.detail}) — {v.result} {v.unit} (this page)</strong>
                                    : <a href={v.href}>{v.label} ({v.detail}) — {v.result} {v.unit} →</a>}
                                {i < arr.length - 1 ? <span> · </span> : null}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Content Grid */}
            <div className="row">
                {/* Main Content Column */}
                <div className="col-12 col-lg-7">
                    <div className={styles['main-content']}>

                        {/* Formula Card */}
                        {this.props.quickAnswer && (
                            <div className={styles['formula-card']}>
                                <div className={styles['formula-card-label']}>Formula</div>
                                <div className={styles['formula-card-content']}>
                                    1 {this.getFromAbbr()} = {this.props.quickAnswer.decimal.value} {this.getToAbbr()}
                                </div>
                            </div>
                        )}

                        {/* How-to Guide */}
                        {this.getPageSummary()}

                        {/* Step-by-step HowTo */}
                        {this.getHowtoSection()}

                        {/* Binary vs Decimal Explainer (digital only) */}
                        {(this.props as any).isDigital && (
                            <div className={styles['info-callout']}>
                                <h3>Decimal (SI) vs Binary (IEC)</h3>
                                <p>
                                    There are two systems for measuring digital data. The <strong>decimal (SI)</strong> system uses powers of 1000 (KB, MB, GB), while the <strong>binary (IEC)</strong> system uses powers of 1024 (KiB, MiB, GiB).
                                </p>
                                <p>
                                    This difference is why a 500 GB hard drive shows roughly 465 GiB in your operating system — the drive is labeled using decimal units, but the OS reports in binary. Both values are correct, just measured differently.
                                </p>
                            </div>
                        )}

                        {/* Common Values Conversion Table */}
                        {this.conversionTable()}

                        {/* Similar Unit Comparison (digital only) */}
                        {(this.props as any).similarUnitComparison && (
                            <div className={styles.section}>
                                <h2>{(this.props as any).similarUnitComparison.decimalAbbr} vs {(this.props as any).similarUnitComparison.binaryAbbr}</h2>
                                <div className={styles['table-responsive']}>
                                    <table className={styles['unit-conversion-table']}>
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>{(this.props as any).similarUnitComparison.decimalPlural} ({(this.props as any).similarUnitComparison.decimalAbbr})</th>
                                                <th>{(this.props as any).similarUnitComparison.binaryPlural} ({(this.props as any).similarUnitComparison.binaryAbbr})</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>Base</td>
                                                <td>{(this.props as any).similarUnitComparison.decimalBase}</td>
                                                <td>{(this.props as any).similarUnitComparison.binaryBase}</td>
                                            </tr>
                                            <tr>
                                                <td>1 {this.getFromAbbr()} =</td>
                                                <td>{(this.props as any).similarUnitComparison.example.decimalResult} {(this.props as any).similarUnitComparison.decimalAbbr}</td>
                                                <td>{(this.props as any).similarUnitComparison.example.binaryResult} {(this.props as any).similarUnitComparison.binaryAbbr}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Page Content */}
                        {this.props.pageContent && (
                            <div className={styles.section}>
                                <div dangerouslySetInnerHTML={{ __html: this.props.pageContent }}></div>
                            </div>
                        )}

                        {/* Unit Descriptions — render whichever side has content (previously
                            required BOTH, so one-sided content shipped as a dead __NEXT_DATA__
                            payload and never rendered). */}
                        {(this.props.fromUnitContent || this.props.toUnitContent) && (
                            <div className={styles.section}>
                                {this.props.fromUnitContent && (
                                    <div className={styles['unit-description']}>
                                        <div dangerouslySetInnerHTML={{ __html: this.props.fromUnitContent }}></div>
                                    </div>
                                )}
                                {this.props.toUnitContent && (
                                    <div className={styles['unit-description']}>
                                        <div dangerouslySetInnerHTML={{ __html: this.props.toUnitContent }}></div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* FAQ Section */}
                        {this.getFaqSection()}

                        {/* Related Conversions */}
                        {pillEntries.length > 0 && (
                            <div className={styles.section}>
                                <h2>People also convert</h2>
                                <div className={styles['related-pills']} data-block={groupBlock}>
                                    {pillEntries.map((link: any, i: number) => (
                                        <Link key={i} href={link.link.replace('/unit-converter/', '/')} className={styles['pill']}>
                                            {link.readableFromAbbr} to {link.readableToAbbr}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Complete Conversion Table */}
                        <div className={styles.section}>
                            <h2>Complete {this.getFromPlural()} conversion table</h2>

                            <div className={styles['live-table-card']}>
                                <div className={styles['live-table-input']}>
                                    <label>Convert</label>
                                    <input
                                        type="number"
                                        ref={this.inputRef}
                                        onInput={() => this.valueChanged()}
                                        value={this.state.inputValue}
                                    />
                                    <span>{unitFormatter(this.props.from?.unit)}</span>
                                </div>
                                <div className={styles['table-responsive']}>
                                    <table className={styles['unit-conversion-table']}>
                                        <thead>
                                            <tr>
                                                <th scope="col">Unit</th>
                                                <th scope="col">Result</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {this.fromTypeSupportedTargetConverters.map(converter => {
                                                // The row for the current conversion must not link to itself; every other row
                                                // links only when the link policy allows it on this page (HV source: whole
                                                // group up to the cap; LV source: HV members + neighbours) — otherwise plain text.
                                                const isSelf = converter.fromUnit === this.props.from?.unit && converter.toUnit === this.props.to?.unit;
                                                const label = `${converter.readableToPlural} (${converter.readableToAbbr})`;
                                                const rowLink = isSelf ? undefined : linkByRow.get(`${converter.readableToPlural}|${converter.readableToAbbr}`);
                                                return (
                                                <tr key={converter.readableName}>
                                                    <td>
                                                        {isSelf
                                                            ? <strong>{label}</strong>
                                                            : rowLink
                                                                ? <Link href={rowLink.replace('/unit-converter/', '/')}>{label}</Link>
                                                                : label}
                                                    </td>
                                                    <td><strong>{this.state[`${converter.measure}***${converter.fromUnit}***${converter.toUnit}`]}</strong> {converter.readableToAbbr}</td>
                                                </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Side Panel Column */}
                <div className="col-12 col-lg-5">
                    <div className={styles['side-panel-wrapper']}>
                        <div className={styles['side-panel']}>
                            {groupEntries.length > 0 && (
                                <>
                                    <h3>{this.props.from.readableType} conversions</h3>
                                    <ul data-block={groupBlock}>
                                        {groupEntries.map((sidePanelLink, i) => (
                                            <li key={`${sidePanelLink.link}${i}`}>
                                                <Link
                                                    className={styles['side-panel-link']}
                                                    href={sidePanelLink.link.replace("/unit-converter/", "")}>
                                                    {unitConversionFormatter(sidePanelLink)}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                            {lb && <PopularUnitBlock measureName={this.props.from.readableType} links={lb.popularUnit} />}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    }

}

// export default withRouter(UnitConverter)
// Functional Wrapper Component
function RouteChangeStateUpdateWrapper(props: any) {
    const router = useRouter();

    if (props.pageFor === 'hub') {
        return <HubPage {...props} />;
    }
    return <UnitConverter pathname={router.asPath} query={router.query} {...props} router={router} />;
}

function HubPage(props: any) {
    return (
        <div style={{marginTop: '1.5rem'}}>
            <Head>
                <link rel="canonical" href={`https://www.xconvert.com/unit-converter/${props.measure}`} />
            </Head>
            <SearchBox />
            <h1>{props.readableName} Conversions</h1>
            <p>{props.conversionCount ?? props.conversions?.length} conversions available for {props.readableName?.toLowerCase()} units.</p>
            {props.conversionCount > (props.conversions?.length || 0) && (
                <p>Showing the most-used {props.readableName?.toLowerCase()} conversions and one entry per unit; every other conversion is listed on its unit's page.</p>
            )}
            <div className={styles['hub-grid']} data-block="hub-list">
                {props.conversions?.map((c: any) => (
                    <Link key={c.id} href={c.url.replace('/unit-converter', '')} className={styles['hub-card']}>
                        <span className={styles['hub-card-abbr']}>{c.fromAbbr} → {c.toAbbr}</span>
                        <span className={styles['hub-card-title']}>{c.title}</span>
                    </Link>
                ))}
            </div>
            {props.related?.length > 0 && (
                <div className={styles['hub-related']}>
                    <h2>Related conversions</h2>
                    <ul>
                        {props.related.map((r: any) => (
                            <li key={r.measure}>
                                <a href={r.url}>{r.readableName} conversions</a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default RouteChangeStateUpdateWrapper;